<template>
  <div class="LKPassportTemplateTableForm">
    <LKPublicationsFormdataTable
      :all_cells="allCells"
      :sheet="activeSheet"
      :list="list"
      :errors="activeSheetErrors"
      :vertical-line-data="verticalLineData"
      :actions="actions"
      :should-update="forceTableUpdate"
      :readonly="readonly"
      :can-edit="!readonly"
      class="LKPassportTemplateTableForm__inner"
      @action="onAction"
      @table:updated="forceTableUpdate = false"
    />

    <v-tabs v-model="activeSheetIndex" dense show-arrows class="LKPassportTemplateTableForm__tabs">
      <v-tab v-for="(item, index) in sheets" :key="index" dense>
        <template #default>
          <span class="LKPassportTemplateTableForm__tab-title">
            <span v-if="item.name.length <= 31" v-text="item.name" />
            <v-tooltip v-else top>
              <template #activator="{ on }">
                <span class="LKPassportTemplateTableForm__tab-title--long" v-on="on" v-text="item.name" />
              </template>

              <slot>
                <span v-text="item.name" />
              </slot>
            </v-tooltip>

            <BInfo
              v-if="item.hint"
              icon="mdi-message-alert-outline"
              icon-class="ml-1"
              :text="item.hint"
              size="1rem"
            />
          </span>
        </template>
      </v-tab>
      <v-tabs-slider />
    </v-tabs>
    <v-layout v-if="isContextualData">
      <v-spacer />
      <v-btn v-if="!readonly" depressed @click="onSave" v-text="'Сохранить'" />
      <v-btn
        v-if="!readonly"
        text
        @click="onSubmit"
      >
        Сдать
      </v-btn>
      <v-btn text @click="$emit('close')" v-text="'Закрыть'" />
    </v-layout>
    <v-layout v-else-if="isSaveVisible">
      <v-spacer />
      <BTooltipButton
        text="Сохранить"
        @click="onSave"
      />
    </v-layout>
  </div>
</template>

<script>
import BInfo from '@/components/common/BInfo.vue'
import BTooltipButton from '@/components/common/BTooltipButton.vue'
import LKPublicationsFormdataTable from '@/views/LKPublications/formdata/components/LKPublicationsFormdataTable.vue'
import { ADD, DELETE, TableActionsTypes } from '@/helpers/enums/table-actions'
import { createCellData, createSheetData, isNewable } from '@/views/LKPublications/formdata/createSheetData'
import { createModalRemove } from '@/helpers/createModalRemove'
import {
  SheetCellDepsTypes,
  SheetCellTypes,
  DEPENDENCE_EMPTY_VALUE,
} from '@/typings/api/FormTemplate'
import { isNaN } from 'lodash'

export default {
  name: 'LKPassportTemplateTable',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BInfo,
    BTooltipButton,
    LKPublicationsFormdataTable,
  },
  props: {
    passport: {
      type: Object,
      required: true,
    },
    detailPassport: {
      type: Object,
      required: true,
    },
    readonly: Boolean,
    isContextualData: Boolean,
  },
  data: (vm) => {
    const contextsData = vm.detailPassport.contexts || []
    const sheets = vm.detailPassport.template
    if (sheets) {
      sheets.forEach(({ cells }, i) => {
        if (!contextsData[i]) {
          contextsData[i] = createSheetData(cells)
        }
      })
    }
    return {
      contextsData,
      activeSheetIndex: 0,
      forceTableUpdate: false,
      isSaveVisible: false,
      errors: {},
    }
  },
  computed: {
    hasErrors() {
      // eslint-disable-next-line no-empty
      if (this.forceTableUpdate) { }
      return Object.keys(this.errors).length > 0
        || (!this.fileUploaded && this.dataCollection[this.activeSheetIndex].length === 0)
        || this.dataCollection[this.activeSheetIndex].length === 0
    },
    activeSheetErrors() {
      return this.errors[this.activeSheetIndex]
    },
    sheets() {
      return this.detailPassport?.template
    },
    activeSheet() {
      return this.sheets[this.activeSheetIndex]
    },
    allCellsByUuid() {
      const { sheets } = this
      const listMap = {}
      sheets
        .map((sheet, index) => {
          // eslint-disable-next-line no-param-reassign
          sheet.$index = index
          // eslint-disable-next-line no-param-reassign
          sheet.$data = {}
          return sheet.cells.map((cell, $index) => {
            // eslint-disable-next-line no-param-reassign
            cell.$sheet = sheet
            // eslint-disable-next-line no-param-reassign
            cell.$index = $index
            return cell
          })
        })
        .flat(1)
        .forEach((cell) => {
          if (!listMap[cell.uuid]) listMap[cell.uuid] = cell
          else listMap[cell.uuid] = { ...listMap[cell.uuid], ...cell }

          if (cell.formula) {
            [...cell.formula.matchAll(/\[#\$_(.+?)#\]/g)].forEach(([, uuid]) => {
              if (!listMap[uuid].usedInCellsWithFormula) listMap[uuid].usedInCellsWithFormula = []
              if (cell.uuid !== uuid && !listMap[uuid].usedInCellsWithFormula.includes(cell.uuid)) {
                listMap[uuid].usedInCellsWithFormula.push(cell.uuid)
              }
            })
          }
        })

      return listMap
    },
    allCells() {
      return Object.values(this.allCellsByUuid)
    },
    list() {
      return this.contextsData[this.activeSheetIndex]
    },
    actions() {
      if (this.readonly) return []
      const newable = isNewable(this.activeSheet.cells)
      return newable
        ? [
          {
            ...ADD,
            getFilter: (data, index, list) => index === (list.length - 1) || list.length === 1,
          },
          {
            ...DELETE,
            getFilter: (data) => !data.$readonly,
          },
        ]
        : []
    },
    /**
     * Данные с вертикальных страниц
     */
    verticalLineData() {
      const data = {}
      this.sheets.filter((sheet) => !!sheet.vertical).forEach((sheet) => {
        data[sheet.uuid] = sheet.$data
      })
      return data
    },
  },
  methods: {
    onSave(event) {
      const toggle = event.toggleLoading
      this.$emit('input', this.contextsData, toggle)
    },
    async onSubmit(event) {
      const updatedData = this.contextsData
      await this.validate(updatedData)
      this.forceTableUpdate = true
      if (Object.keys(this.errors).length > 0) {
        this.$notice.warning('Не все обязательные поля заполнены', { type: 'pink' })
        return
      }
      const toggle = event.toggleLoading
      this.$emit('submit', this.contextsData, toggle)
    },
    async onAction(action, data, meta) {
      switch (action) {
        case TableActionsTypes.add: {
          const cell = createCellData(this.activeSheet.cells)
          this.list.push(cell)
          this.forceTableUpdate = true
          break
        }

        case TableActionsTypes.delete: {
          const propsData = { titleText: 'Удалить строку' }
          const modal = await createModalRemove(this, propsData)
          const result = await modal.open()
          if (!result) return

          const index = this.list.indexOf(data)
          this.list.splice(index, 1)

          if (!this.list.length) {
            const cell = createCellData(this.activeSheet.cells)
            this.list.push(cell)
          }
          this.forceTableUpdate = true
          break
        }

        case TableActionsTypes.edit: {
          this.list[meta.index] = data
          const updatedData = this.contextsData
          await this.validate(updatedData)
          this.forceTableUpdate = true
          // await this.validateSheet(this.activeSheet, this.list, meta.index)
          break
        }

        default:
      }
      this.isSaveVisible = true
    },
    validateCell(row_cells, row_values, cell, value) {
      const { type } = cell
      const valueIsEmpty = value === null || value === undefined || value === ''
      if (valueIsEmpty) {
        if (type === SheetCellTypes.formula) return null
        if (!cell.required) return null
        if (!cell.dependence.type) return 'Ячейка обязательна для заполнения'
      }
      if (cell.dependence.type) {
        const validateResults = cell.dependence.list.map((dependence) => {
          const refCellIndex = row_cells.findIndex(({ uuid }) => uuid === dependence.cell_uuid)
          const refValue = row_values[refCellIndex]
          if (refValue === null || refValue === undefined || refValue === '') {
            return dependence.list.includes(DEPENDENCE_EMPTY_VALUE)
          }
          return dependence.list.includes(refValue.toString())
        })
        const isValueInList = cell.dependence.type === SheetCellDepsTypes.some
          ? validateResults.some(Boolean) : validateResults.every(Boolean)
        if (isValueInList && valueIsEmpty && cell.required) {
          return 'Ячейка обязательна для заполнения'
        } if (!isValueInList) {
          return valueIsEmpty ? null : 'Ячейка не может быть заполнена'
        }
      }
      return null
    },
    async validateForm(data) {
      const errors = []
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.sheets.length; i++) {
        const sheet = this.sheets[i]
        const sheetData = data[i]
        let isAnyNonEmptyObject = false

        if (!sheet.required) {
          for (let j = 0; j < sheetData.length; j += 1) {
            if (Object.keys(sheetData[j]).length > 0) {
              isAnyNonEmptyObject = true
              break
            }
          }
          if (!isAnyNonEmptyObject) {
            break
          }
        }

        // eslint-disable-next-line no-plusplus
        for (let rowIndex = 0; rowIndex < sheetData.length; rowIndex++) {
          const row = sheetData[rowIndex]
          // eslint-disable-next-line no-plusplus
          for (let j = 0; j < sheet.cells.length; j++) {
            const cell = sheet.cells[j]
            const value = row[cell.$index || j]

            // eslint-disable-next-line no-await-in-loop
            const validationError = await this.validateCell(sheet.cells, row, cell, value)
            if (validationError) {
              if (this.activeSheet.vertical) {
                errors.push(`${i}|${j}:${rowIndex}| ${validationError}`)
              } else {
                errors.push(`${i}|${rowIndex}:${j}| ${validationError}`)
              }
            }
          }
        }
      }
      return errors
    },
    async reset() {
      this.errors = {}

      await this.$validator.reset()
    },
    parseErrorsArr(errors) {
      const errorsMap = {}
      // eslint-disable-next-line consistent-return
      errors.forEach((error) => {
        const [list, cell, text] = error.split('|')
        const [row, col] = cell.split(':')
        const textTrimmed = text.trim()
        if (!errorsMap[list]) errorsMap[list] = {}
        if (!errorsMap[list][textTrimmed]) errorsMap[list][textTrimmed] = {}
        if (!errorsMap[list][textTrimmed][row]) errorsMap[list][textTrimmed][row] = []
        errorsMap[list][textTrimmed][row].push(col)
      })

      return errorsMap
    },
    parseErrorsArrForListView(errors) {
      const errorsMap = {}
      // eslint-disable-next-line consistent-return
      errors.forEach((error) => {
        const [list, cell, text] = error.split('|')
        const [row, col] = cell.split(':')
        const hasCoords = !isNaN(row) && !isNaN(col) && row >= 0 && col >= 0

        if (!errorsMap[list]) errorsMap[list] = []
        if (hasCoords) {
          let numberCol = parseInt(col, 10)
          if (this.activeSheet.vertical) {
            numberCol += 1
          }
          const colFirst = Math.trunc(numberCol / 26) > 0 ? (Math.trunc(numberCol / 25) + 9).toString(36).toUpperCase() : ''
          const colSecond = (Math.trunc(numberCol % 26) + 10).toString(36).toUpperCase()

          if (this.activeSheet.vertical) {
            errorsMap[list].push(`${Number(row) + 1}:${colFirst}${colSecond} - ${text}`)
          } else {
            errorsMap[list].push(`${Number(row) + 2}:${colFirst}${colSecond} - ${text}`)
          }
        } else {
          errorsMap[list].push(text)
        }
      })
      return errorsMap
    },
    async validate(data) {
      await this.reset()
      const errors = await this.validateForm(data || this.dataCollection)
      this.errors = this.parseErrorsArr(errors)
      this.textPreparedErrors = this.parseErrorsArrForListView(errors)
      this.isCorrect = !errors.length
      return !errors.length
    },
  },
}
</script>

<style lang="scss">
.LKPassportTemplateTableForm {
  &__empty {
    padding: 40px 20px;
    text-align: center;
  }

  &__inner {
    overflow: auto;
  }

  &__tabs {
    max-width: 1000px;
  }

  &__tab-title {
    display: flex;

    &--long {
      display: inline-block;
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__link {
    color: $webflow-color-blue-dark-hover;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
